import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Grid2, Box, Card, CardContent } from "@mui/material";

import { Layout } from "Layout";

import { CustomFields } from "components/CustomFields/CustomFields";
import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { QuickAddVendor } from "components/QuickAddVendor";
import { SectionHeading } from "components/SectionHeading";
import { LineItemTotals } from "components/formFields/Totals/Purchasing/LineItemTotals";
import {
  FrmDeposit,
  FrmLocation,
  FrmNumber,
  FrmPayment,
  FrmTerms,
  FrmVendorWithAdd,
  FrmVendorNotes,
  FrmComment,
  FrmDepartment,
  FrmCurrencyExchangeRate,
  FrmAutoSerialLots,
} from "components/formFields/frm";
import { FrmCheckboxWithHoverHelp } from "components/formFields/frm/FrmCheckboxWithHoverHelp";
import { PAYMENT_TYPE_BILL_ID } from "components/formFields/frm/FrmPayment";
import { FrmSignature } from "components/formFields/frm/FrmSignature";
import { DatePicker } from "components/html/Input/DatePicker";
import { DateTimePicker } from "components/html/Input/DateTimePicker";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { HoverHelp } from "components/utility/HoverHelp";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";
import { WarningModal } from "components/utility/WarningModal";

import { i18n } from "services/i18nService";
import { getAllBinsForLocation } from "services/sosInventoryService/domainLogic";
import {
  updateTotals,
  ZERO_TOTALS,
} from "services/sosInventoryService/purchasingTransaction/updateTotals";
import { setPageDirty, handleRefreshExchangeRate } from "services/utility/edit";

import { useClasses } from "hooks/useClasses";
import { useCustomFieldDefsAsObject } from "hooks/useCustomFieldDefinitions";
import { useErrors } from "hooks/useErrors";
import { useItems } from "hooks/useItems";
import { useLineHandler } from "hooks/useLineHandler";
import { usePlans } from "hooks/usePlans";
import { useRelated } from "hooks/useRelated";
import { useTaxCodes, TAX_CODE_TYPE_PURCHASE } from "hooks/useTaxCodes";
import { useVendors } from "hooks/useVendors";

import { AddPurchaseOrder } from "views/ItemReceipts/ItemReceipt/ItemReceipt/AddPurchaseOrder";
import { CalculateDueDateSwitch } from "views/ItemReceipts/ItemReceipt/ItemReceipt/CalculateDueDateSwitch";
import { OtherCosts } from "views/ItemReceipts/ItemReceipt/ItemReceipt/OtherCosts";
import {
  getTransaction,
  updateTransaction,
} from "views/ItemReceipts/ItemReceipt/ItemReceipt/controller";
import { LineItems } from "views/PurchaseTransactions/LineItems";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import { theme } from "SosTheme";
import { OBJECT_TYPES, ITEM_QUICKLIST_CASES } from "appConstants";
import { EMPTY_LINE_ITEM, EMPTY_OTHER_COST } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.ITEM_RECEIPT.fullString;
const CUSTOM_FIELD_OBJECT_TYPES = [OBJECT_TYPE, OBJECT_TYPES.VENDOR.fullString];

export function ItemReceipt(props) {
  const { id, onClose, newFromObjectType, newFromId } = props;

  // TRANSACTION STATE
  const [record, setRecord] = useState();
  const [recordToReload, setRecordToReload] = useState(null);
  const [lines, lineHandler] = useLineHandler();
  const [otherCosts, otherCostHandler] = useLineHandler();
  const [totals, setTotals] = useState(ZERO_TOTALS);
  const [relatedRecords, setRelatedRecords] = useState({});
  const CURRENT_STATE = {
    record,
    lines,
    relatedRecords,
    totals,
  };

  // UI STATE
  const { errors, setErrors, isInError } = useErrors();
  const [clearLines, setClearLines] = useState(false);
  const [clearOtherCosts, setClearOtherCosts] = useState(false);
  const customFieldDefs = useCustomFieldDefsAsObject(CUSTOM_FIELD_OBJECT_TYPES);
  const [addNewVendor, setAddNewVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [vendorAdded, setVendorAdded] = useState(0);
  const [itemAdded, setItemAdded] = useState(0);
  const loadingState = useSelector((state) => state.loading.editModal);

  // RELATED RECORD LISTS
  const taxCodes = useTaxCodes(TAX_CODE_TYPE_PURCHASE);
  const currencies = useRelated("currency");
  const { classes, classesCount } = useClasses();
  const vendors = useVendors(vendorAdded);
  const [bins, setBins] = useState(null);
  const { items, itemsCount } = useItems(
    ITEM_QUICKLIST_CASES.PURCHASING,
    itemAdded
  );

  // SETTINGS
  const departmentEnabled = useSelector(
    (state) => state.userCompanySettings.settings.departmentEnabled
  );
  const { hasAtLeastPlusPlan, hasAtLeastProPlan } = usePlans();
  const multiCurrencyEnabled = useSelector(
    (state) => state.userCompanySettings.settings.multiCurrencyEnabled
  );

  // OTHER SETUP
  const dispatch = useDispatch();

  // UPDATERS FOR CONTROLLER TO UPDATE STATE
  const UPDATERS = useMemo(
    () => ({
      setRecord,
      lineHandler,
      setRelatedRecords,
      otherCostHandler,
      setErrors,
    }),
    [lineHandler, otherCostHandler, setErrors]
  );

  // LOAD INITIAL TRANSACTION STATE
  useEffect(() => {
    async function _getTransaction() {
      dispatch(editModalLoadingIndicatorOn());
      await getTransaction(
        id,
        newFromId,
        newFromObjectType,
        UPDATERS,
        customFieldDefs[OBJECT_TYPE],
        recordToReload
      );
      dispatch(editModalLoadingIndicatorOff());
    }
    if (customFieldDefs) {
      _getTransaction();
    }
  }, [
    dispatch,
    id,
    newFromId,
    newFromObjectType,
    lineHandler,
    UPDATERS,
    customFieldDefs,
    recordToReload,
  ]);

  // HANDLE ALL USER INPUT
  function handleInputFieldChange(field, newValue) {
    setPageDirty();
    updateTransaction(
      field,
      newValue,
      customFieldDefs[OBJECT_TYPE],
      CURRENT_STATE,
      UPDATERS,
      currencies
    );
  }

  useEffect(() => {
    // when location changes, get bins for that location
    async function _getBins() {
      if (record?.location?.id) {
        setBins(await getAllBinsForLocation(record.location.id));
      }
    }
    _getBins();
  }, [record?.location]);

  useEffect(
    () => updateTotals(lines, setTotals, taxCodes, record?.currency?.name),
    [lines, taxCodes, record?.currency?.name]
  );

  async function handleAddNewVendor(vendor) {
    setPageDirty();
    handleInputFieldChange("vendor", vendor);
    setVendorAdded((prev) => prev + 1);
    setAddNewVendor(false);
    return;
  }

  function doClearLines() {
    setPageDirty();
    lineHandler({ type: "clear", blankLine: EMPTY_LINE_ITEM[OBJECT_TYPE] });
    setClearLines(false);
  }

  function doClearOtherCosts() {
    setPageDirty();
    otherCostHandler({
      type: "clear",
      blankLine: EMPTY_OTHER_COST[OBJECT_TYPE],
    });
    setClearOtherCosts(false);
  }

  const isBillPayment = record?.payment?.id === PAYMENT_TYPE_BILL_ID;

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        setReloadRecord={setRecordToReload}
        objectType={OBJECT_TYPE}
        relatedRecords={relatedRecords}
        lines={lines}
        totals={totals}
        otherCosts={otherCosts}
        setErrors={setErrors}
        errors={errors}
        text={record?.number}
        handleClose={onClose}
        vendorId={record?.vendor?.id}
      />
      {loadingState && <Loading />}
      <ObjectEditDialogContent>
        <Layout
          pageTitle={
            id
              ? `${i18n("objectType.itemreceipt.Sentence")} ${
                  record?.number || ""
                }`
              : i18n("objectType.itemreceipt.New")
          }
        >
          <div style={{ margin: theme.spacing(2) }}>
            <FormErrors errors={errors} setErrors={setErrors} />
            {record ? (
              <>
                <FrmFieldGrid gridAutoFlow="dense">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DateTimePicker
                      value={record.date}
                      name="date"
                      onValueChange={handleInputFieldChange}
                      label={i18n("frmLabel.Date")}
                      error={isInError("date")}
                      dataTesting="date"
                    />
                    {isBillPayment && (
                      <HoverHelp
                        title={i18n("hoverHelp.itemReceipt.DateField")}
                      />
                    )}
                  </Box>
                  <FrmNumber
                    objectType={OBJECT_TYPE}
                    recordId={record.id}
                    label={i18n("itemReceipt.labelNumber")}
                    value={record.number}
                    onValueChange={handleInputFieldChange}
                    error={isInError("number")}
                  />
                  {hasAtLeastPlusPlan && (
                    <Box sx={{ gridColumn: "1 / 2" }}>
                      <FrmLocation
                        value={record.location}
                        error={isInError("location")}
                        onValueChange={handleInputFieldChange}
                        width="25rem"
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      gridColumn: "1 / 2",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <FrmVendorWithAdd
                      value={record.vendor}
                      onValueChange={handleInputFieldChange}
                      setAddNewVendor={setAddNewVendor}
                      setNewVendorName={setNewVendorName}
                      vendors={vendors}
                      error={isInError("vendor")}
                    />
                    <FrmVendorNotes
                      value={record.vendorNotes}
                      onValueChange={handleInputFieldChange}
                    />
                  </Box>
                  <Box sx={{ gridColumn: "2 / 2" }}>
                    <FrmPayment
                      value={record.payment}
                      onValueChange={handleInputFieldChange}
                      error={isInError("payment")}
                    />
                  </Box>
                  {isBillPayment && (
                    <>
                      <Card sx={{ mt: 1 }} variant="outlined">
                        <CardContent sx={{ "&:last-child": { pb: "16px" } }}>
                          <Box
                            sx={{
                              gridColumn: "2 / 2",
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <FrmTerms
                              maxWidth="20rem"
                              minWidth="20rem"
                              value={record.terms}
                              onValueChange={handleInputFieldChange}
                              error={isInError("terms")}
                              textSx={{ margin: "0.5rem 0 0 0" }}
                            />
                            <Box sx={{ display: "flex" }}>
                              <CalculateDueDateSwitch
                                value={record.calculateDueDateFromInvoiceDate}
                                onValueChange={handleInputFieldChange}
                              />
                            </Box>
                            {record.calculateDueDateFromInvoiceDate && (
                              <Box>
                                <DatePicker
                                  key={record.vendorInvoiceDate}
                                  name="vendorInvoiceDate"
                                  value={record.vendorInvoiceDate}
                                  label={i18n("frmLabel.VendorInvoiceDate")}
                                  onValueChange={handleInputFieldChange}
                                  dataTesting="vendorInvoiceDate"
                                  textSx={{ margin: "0.5rem 0 0 0" }}
                                />
                              </Box>
                            )}
                            <DatePicker
                              name="vendorInvoiceDueDate"
                              value={record.vendorInvoiceDueDate}
                              label={i18n("frmLabel.BillDueDate")}
                              onValueChange={handleInputFieldChange}
                              textSx={{ margin: "0.5rem 0 0 0" }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </FrmFieldGrid>
                <VSpace space={1} />
                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    {multiCurrencyEnabled && (
                      <Grid2 size={4}>
                        <FrmCurrencyExchangeRate
                          customerOrVendor={record.vendor}
                          record={record}
                          currencies={currencies}
                          currencyError={isInError("currency")}
                          exchangeRate={record.exchangeRate}
                          handleInputChange={handleInputFieldChange}
                          handleRefreshExchangeRate={() =>
                            handleRefreshExchangeRate(
                              currencies,
                              record,
                              handleInputFieldChange
                            )
                          }
                        />
                      </Grid2>
                    )}
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <AddPurchaseOrder
                      location={record.location}
                      data={record.date}
                      vendor={record.vendor}
                      lineHandler={lineHandler}
                    />
                  </div>
                </FrmFieldGrid>
                <VSpace space={1} />
                <LineItems
                  showAppliedTo
                  showBinSerialAndLot
                  record={record}
                  objectType={OBJECT_TYPE}
                  lines={lines}
                  totals={totals}
                  taxCodes={taxCodes}
                  items={items}
                  itemsCount={itemsCount}
                  bins={bins}
                  addItem={() => setItemAdded((prev) => prev + 1)}
                  classes={classes}
                  classesCount={classesCount}
                  lineHandler={lineHandler}
                  location={relatedRecords.location}
                  lineItemsErrors={errors.line}
                  setClearLines={setClearLines}
                />

                <VSpace space={1} />

                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmAutoSerialLots
                      value={record.autoSerialLotNumbering}
                      onValueChange={handleInputFieldChange}
                    />
                    <FrmCheckboxWithHoverHelp
                      name="updateDefaultCosts"
                      value={record.updateDefaultCosts}
                      onValueChange={handleInputFieldChange}
                      label={i18n("frmLabel.UpdateDefaultCosts")}
                      helpText={i18n("hoverHelp.UpdateDefaultCosts")}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <LineItemTotals totals={totals} />
                  </div>
                </FrmFieldGrid>

                {hasAtLeastProPlan && (
                  <>
                    <>
                      <SectionHeading>
                        {i18n("itemReceipt.OtherCostsHeader")}
                      </SectionHeading>
                      <OtherCosts
                        otherCosts={otherCosts}
                        otherCostHandler={otherCostHandler}
                        items={items}
                        itemsCount={itemsCount}
                        vendors={vendors}
                        classes={classes}
                        classesCount={classesCount}
                        recordVendorId={record.vendor?.id}
                        setClearOtherCosts={setClearOtherCosts}
                        lineHandler={otherCostHandler}
                        objectType={OBJECT_TYPE}
                      />
                    </>
                    <VSpace space={2} />
                  </>
                )}

                <VSpace space={4} />
                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmDeposit
                      maxWidth="20rem"
                      amountValue={record.depositAmount}
                      percentValue={record.depositPercent}
                      onValueChange={handleInputFieldChange}
                      amountError={isInError("depositAmount")}
                      percentError={isInError("depositPercent")}
                    />
                  </div>
                  {departmentEnabled && (
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmDepartment
                        maxWidth="20rem"
                        value={record.department}
                        onValueChange={handleInputFieldChange}
                      />
                    </div>
                  )}
                  <div style={{ gridColumn: "1 / 2", gridRow: "span 2" }}>
                    <FrmComment
                      value={record.comment}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmSignature
                      objectType={OBJECT_TYPE}
                      id={record.id}
                      hasSignature={record.hasSignature}
                    />
                  </div>
                </FrmFieldGrid>

                <CustomFields
                  customFieldDefinitions={customFieldDefs[OBJECT_TYPE]}
                  customFields={record.customFields}
                  onValueChange={handleInputFieldChange}
                  setErrors={setErrors}
                />
              </>
            ) : (
              !isInError("record") && (
                <FixedLoadingIndicator text={`${i18n("global.Loading")}...`} />
              )
            )}
            <VSpace space={4} />
          </div>
          {record && (
            <>
              {!!addNewVendor && (
                <QuickAddVendor
                  open
                  initialNameValue={newVendorName}
                  onClose={() => setAddNewVendor(false)}
                  onAdd={handleAddNewVendor}
                />
              )}
              <WarningModal
                title={i18n("global.ClearLines")}
                message={i18n("global.clearLinesMessage")}
                open={clearLines}
                onOk={doClearLines}
                onClose={() => setClearLines(false)}
              />

              <WarningModal
                title={i18n("global.clearOtherCostsTitle")}
                message={i18n("global.clearOtherCostsMessage")}
                open={clearOtherCosts}
                onOk={doClearOtherCosts}
                onClose={() => setClearOtherCosts(false)}
              />
            </>
          )}
        </Layout>
      </ObjectEditDialogContent>
    </>
  );
}
